import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useQuery from 'hooks/apollo/useQuery';
import { LINK_ITEMS } from 'features/common/queries';
import { addItemsModalOpenVar } from '../../variables';
import { UnpaddedDialog } from 'components/common/Styles';
import FlexBox from 'components/common/FlexBox';
import { Button, TextField, Typography } from '@totalsoft/rocket-ui';
import { Chip, Icon, Tooltip } from '@mui/material';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { SelectCheckbox } from '../../Styles';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import IconButton from 'components/common/buttons/IconButton';

const itemRowHeight = 50;

const ResidualValueGridAddItemsComponent = ({ existingItemIds, onAdd }) => {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');

  const { loading } = useQuery(LINK_ITEMS, {
    variables: { retrieveAll: true },
    onCompleted: data => {
      setItems(
        (data?.linkItems || []).filter(item => !existingItemIds.includes(item.id)).map(item => ({ ...item, selected: false, details: [] }))
      );
    }
  });

  const selectItem = useCallback(
    id => value => {
      setItems(prev => prev.map(item => (item.id === id ? { ...item, selected: value } : item)));
    },
    []
  );

  const clearSelection = useCallback(() => {
    setItems(prev => prev.map(item => ({ ...item, selected: false })));
  }, []);

  const addItems = useCallback(() => {
    const selectedItems = items.filter(item => item.selected).map(item => ({ ...item, selected: false }));
    onAdd(selectedItems);
  }, [onAdd, items]);

  const close = useCallback(() => {
    addItemsModalOpenVar(false);
  }, []);

  const filteredItems = useMemo(() => items.filter(item => item.name.toLowerCase().includes(search.toLowerCase())), [items, search]);

  const [hasSelection, selectionCount] = useMemo(() => {
    const selectedItems = items.filter(item => item.selected);
    return [selectedItems.length > 0, selectedItems.length];
  }, [items]);

  const ItemRow = useCallback(
    ({ index, style }) => {
      const item = filteredItems[index];
      return (
        <FlexBox key={item.id} height={itemRowHeight} alignItems='center' style={style}>
          <FlexBox basis={75} shrink={0}>
            <SelectCheckbox checked={item.selected} onChange={onCheckBoxChange(selectItem(item.id))} />
          </FlexBox>
          <Tooltip title={item.name} placement='bottom-start'>
            <FlexBox overflow='hidden'>
              <Typography variant='subtitle2' noWrap>
                {item.name}
              </Typography>
            </FlexBox>
          </Tooltip>
        </FlexBox>
      );
    },
    [filteredItems, selectItem]
  );

  return (
    <UnpaddedDialog
      id='add-residual-grid-value-items-dialog'
      open={true}
      fullWidth
      maxWidth='sm'
      scroll={'body'}
      TransitionProps={{ exit: false }}
      closeButtonProps={{ sx: { display: 'none' } }}
      fullScreen={false}
      onClose={close}
      content={
        <FlexBox direction='column' gap={2} px={3}>
          <FlexBox px={2} alignItems='end' gap={1}>
            <TextField
              fullWidth
              label={t('Search')}
              value={search}
              onChange={setSearch}
              startAdornment={<Icon sx={{ mr: 1 }}>search</Icon>}
              disabled={loading}
              debounceBy={200}
              isClearable
            />
            <FlexBox alignItems='center' gap={0.5}>
              <Chip label={`${selectionCount} selected`} color='primary' />
              <IconButton icon='remove_done' tooltip={t('ClearSelection')} onClick={clearSelection} />
            </FlexBox>
          </FlexBox>
          {loading && <WrappedFakeText />}
          {!loading && filteredItems.length === 0 && <Typography align='center'>{t('ListBrowser.NoEntries')}</Typography>}
          {!loading && filteredItems.length > 0 && (
            <FixedSizeList
              height={Math.min(itemRowHeight * filteredItems.length, 400)}
              itemCount={filteredItems.length}
              itemSize={itemRowHeight}
            >
              {ItemRow}
            </FixedSizeList>
          )}
          <FlexBox gap={1} justifyContent='end'>
            <Button disabled={!hasSelection} onClick={addItems}>
              {t('Add')}
            </Button>
            <Button onClick={close}>{t('Cancel')}</Button>
          </FlexBox>
        </FlexBox>
      }
    />
  );
};

ResidualValueGridAddItemsComponent.propTypes = {
  existingItemIds: PropTypes.array,
  onAdd: PropTypes.func
};

export default ResidualValueGridAddItemsComponent;
